/**
 * Irish catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Irish (Gaeilge) at a mathematician's register. **Warrant → «Barántas»** — the
 * native Irish term for warrant/guarantee, a strong fit. Irish has the richest CLDR cardinal
 * set in this batch — one / two / few / many / other (one 1, two 2, few 3–6, many 7–10,
 * other 0 & 11+) — so `frameActive` declares all five (plus an explicit `=0`).
 *
 * NOTE (honesty over guessing): Irish numeral–noun agreement (lenition/eclipsis, singular
 * after numerals) is intricate; the inflected `frameActive` forms here are best-effort and
 * are flagged "expert-review suggested" in `Agent9_REPORT.md`. W/M grades stay Latin
 * (`::numbering-system/latn`). Per INV-STABLE-ID any correction changes only the string and
 * leaves every verdict fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const GA_CATALOG = {
    locale: 'ga',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Fiosrúchán',
        [P2.sectionClaims]: 'Ráitis',
        [P2.sectionIntegrity]: 'Iomláine',
        [P2.axisWarrantLabel]: 'Barántas',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Eimpíreach',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Sainmhíniú} interp {Léirmhíniú} testable {Intástáilte} tested {Tástáilte} refuted {Bréagnaithe} program {Clár taighde} philos {Fealsúnach} other {Anaithnid}}',
        [P2.axisModalLabel]: 'Módúil',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Leibhéal nirt {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Inscrúdaithe',
        [P2.stateRefuted]: 'Bréagnaithe',
        [P2.stateOutside]: 'Lasmuigh den scóip',
        [P2.verdictConflict]: 'I gcoinbhleacht le {constraints}',
        [P2.verdictOutside]: 'Teastaíonn {missing}',
        [P2.lossRegisterPlain]: 'Simplithe go gnáthteanga — fágtar cuid den chruinneas ar lár.',
        [P2.lossRegisterKid]: 'Simplithe do léitheoirí óga — fágtar go leor sonraí ar lár.',
        [P2.lossProfileDetail]: 'Déantar an phróifíl iomlán Barántas / Eimpíreach / Módúil a achoimriú anseo.',
        [P2.lossConflictDetail]: 'Tá sonraí na coinbhleachta giorraithe san amharc seo.',
        [P2.laneVerified]: 'Fíoraithe',
        [P2.laneUnverified]: 'Neamhfhíoraithe',
        [P2.laneConflict]: 'Coinbhleacht cruthaithe',
        [P2.constraintLogic]: 'Loighic',
        [P2.constraintLightBends]: 'Lúbann an solas i réimse imtharraingthe',
        [P2.constraintParallelPostulate]: 'Postaláid na líne comhthreomhaire',
        [P2.claimScalarGravity]: 'Teoiric scálach na himtharraingthe',
        [P2.claimNonEuclidean]: 'Céimseata neamh-Eoiclídeach',
        [P2.claimSubstrateSr]: 'Léirmhíniú fo-shraithe ar an gcoibhneasacht speisialta',
        [P2.edgeExtObservation]: 'Breathnóireacht sheachtrach',
        [P2.edgeResourceSample]: 'Acmhainn ríofa roinnte',
        [P2.edgeHeuristicSample]: 'Treoir sheiftiúil',
        [P2.edgeSharedSample]: 'Bunús roinnte',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Srianta',
        [P2.sectionEdges]: 'Imill',
        [P2.sectionFrame]: 'Fráma',
        [P2.sectionExposure]: 'Nochtadh',
        [P2.statusAdoptedFormal]: 'Glactha (foirmiúil)',
        [P2.statusAdoptedInterpretive]: 'Glactha (léirmhínitheach)',
        [P2.statusSuspended]: 'Ar fionraí',
        [P2.edgeEffectConstraint]: 'Cumann srian (is féidir a bhréagnú)',
        [P2.edgeEffectResource]: 'Acmhainn — gan éifeacht ar bhreithiúnais',
        [P2.edgeEffectHeuristic]: 'Seift — gan éifeacht ar bhreithiúnais',
        [P2.edgeEffectShared]: 'Bunús roinnte — gan éifeacht ar bhreithiúnais',
        [P2.frameActive]: '{count, plural, =0 {Gan aon srian gníomhach} one {# srian gníomhach} two {# shrian ghníomhach} few {# srian ghníomhach} many {# srian ghníomhach} other {# srianta gníomhacha}}',
        [P2.exposureNone]: 'Gan spleáchais léirmhínitheacha',
        [P2.exposureDep]: 'Ag brath ar {id}',
        [P2.laneTrackMachine]: 'Meaisín',
        [P2.laneTrackExpert]: 'Saineolaí',
        [P2.laneTrackCommunity]: 'Pobal',
        [P2.expertAudited]: 'Iniúchta',
        [P2.expertInProgress]: 'Ar siúl',
        [P2.expertNotReviewed]: 'Gan athbhreithniú',
        [P2.communityFlags]: 'Tá bratacha ann',
        [P2.communityNone]: 'Gan bratacha',
        [P2.controlChallenge]: 'Dúshlán',
        [P2.controlActivate]: 'Gníomhachtaigh',
        [P2.controlMakeInterpretive]: 'Déan léirmhínitheach',
        [P2.controlImportEdge]: 'Iompórtáil imeall',
        [P2.controlPromote]: 'Ardaigh',
        [P2.controlUndo]: 'Cealaigh',
        [P2.controlRedo]: 'Athdhéan',
        [P2.controlRegister]: 'Cláraigh',
        [P2.promoteRejected]: 'Ardú diúltaithe',
        [P2.promoteAccepted]: 'Ardú glactha',
        [P2.promoteReasonRefutationTerminal]: 'Ní féidir le cruthúnas bréagnú a leigheas.',
        [P2.promoteReasonKeystone]: 'Teastaíonn tacaíocht eimpíreach le hardú lárchloiche.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Níl an cineál eimpíreach socraithe.',
        [P2.promoteReasonModalNotFixed]: 'Níl an chéim mhódúil socraithe.',
        [P2.promoteReasonWarrantNotImproved]: 'Níor feabhsaíodh an chéim bharántais.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ga.js.map